import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { FIRST_ELEMENT_INDEX, NumberUtils, USER_INPUT_DEBOUNCE_TIME } from '@modeso/aumico-lib-core-fe';
import {
    IAccount,
    IAopProfitCalculation,
    IAppropriationOfProfitEntry,
    IBalanceSwitchAccount,
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep } from 'lodash-es';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { localizationKeys } from '../../constants/localizationKeys';
import { IProfitCalculationTableData } from '../../interfaces/IProfitCalculationTableData.interface';

const debug = Debug('aumico:aumico-lib-annualreport-fe:ProfitCalculationComponent');

const SELECTED_ACCOUNT_INDEX = 0;

const PROFIT_ROW_NAME = 'Annual Profit';
const AVAILABLE_RETAINED_EARNINGS_ROW_NAME = 'Available Retained Earnings';
@UntilDestroy()
@Component({
    selector: 'aumico-annualReport-profit-calculation-component',
    templateUrl: 'profit-calculation.component.html',
    styleUrls: ['profit-calculation.component.scss'],
})
export class ProfitCalculationComponent implements OnInit, OnChanges {
    @Output() profitCalculationOutput = new EventEmitter<IAopProfitCalculation>();
    @Output() profitCalculationCurrentRetainedEarnings = new EventEmitter<number>();
    @Output() profitCalculationPreviousRetainedEarnings = new EventEmitter<number>();
    @Output() profitCalculationPrefilledValue = new EventEmitter<IAopProfitCalculation>();

    @Input() currentAnnualProfit: number | null = null;

    @Input() previousAnnualProfit: number | null = null;

    private _balanceAccounts: (IAccount & { extraLabel?: string })[] = [];
    @Input()
    set balanceAccounts(accounts: IAccount[]) {
        // Count occurrences of each account name
        const nameCount = accounts.reduce((acc, account) => {
            acc[account.name] = (acc[account.name] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);

        const assetsTitle = this.localizedTexts.t('aumico_templates_group_account_mapping_fli_type_assets', {
            lng: this.reportLocale,
        }) as string;

        const liabilitiesTitle = this.localizedTexts.t('aumico_templates_group_account_mapping_fli_type_liabilities', {
            lng: this.reportLocale,
        }) as string;

        this._balanceAccounts = accounts.map((account) => ({
            ...account,
            extraLabel:
                nameCount[account.name] > 1
                    ? (account as IBalanceSwitchAccount).originsFromAsset
                        ? assetsTitle
                        : liabilitiesTitle
                    : undefined,
        }));
    }

    get balanceAccounts(): (IAccount & { extraLabel?: string })[] {
        return this._balanceAccounts;
    }

    @Input() currentYearDate: string | undefined;

    @Input() previousYearDate: string | undefined;

    @Input() currency: string;
    // refer to finNumber pipe for reference on values
    @Input() numberFormat = '1.2-2';
    // we have it as input incase of edit mode
    @Input() savedProfitCalculation: IAopProfitCalculation | undefined;

    private profitCalculationOutputData: IAopProfitCalculation | undefined;

    @Input() previousProfitCalculation: IAopProfitCalculation | undefined;

    @Input() reportLocale: string;

    form: UntypedFormGroup;
    dataSource: UntypedFormArray;

    readonly displayedColumns = ['name', 'currentValue', 'previousValue', 'remove'];
    annualProfitName = new UntypedFormControl(PROFIT_ROW_NAME);
    accountClosingAmount: number | null;
    accountOpeningAmount: number | null;
    currentAvailableRetainedEarnings: number | null = null;
    previousAvailableRetainedEarnings: number | null = null;
    private formChanges: Subscription | undefined;
    private annualProfitNameChange$: Subscription;
    public availableRetainedEarningText: string;
    public profitCalcaulationHeaderText: string;
    protected profitCalculationTableData: IProfitCalculationTableData[] = [];
    private local = localizationKeys;
    subscription: Subscription;
    private initilizedWithData = false;
    private changeProfitLabel = undefined;

    constructor(private formBuilder: UntypedFormBuilder, private localizedTexts: I18NextService) {
        this.initializeForm();
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (changes['savedProfitCalculation']?.currentValue && this.currentAnnualProfit != null) {
            this.fillFormWithSavedData();
            this.profitCalculationPrefilledValue.emit(this.profitCalculationOutputData);
        }

        if (this.initilizedWithData) {
            return;
        }

        // Check if annual profit values changed and update label accordingly
        if (changes['currentAnnualProfit'] || changes['previousAnnualProfit']) {
            // Update the profit label and header if not in edit mode
            if (this.savedProfitCalculation == null && this.changeProfitLabel == null) {
                this.annualProfitName.setValue(this.getDynamicProfitLabel(), { emitEvent: false });
                this.profitCalcaulationHeaderText = this.getDynamicProfitCalculationHeader();
            }
        }

        if (this.currentAnnualProfit != null && this.savedProfitCalculation != null) {
            this.fillFormWithSavedData();
            this.initilizedWithData = true;
            this.profitCalculationPrefilledValue.emit(this.profitCalculationOutputData);
        } else if (this.previousAnnualProfit != null && this.previousProfitCalculation != null) {
            this.fillFormWithAutofillData();
            this.initilizedWithData = true;
            this.profitCalculationPrefilledValue.emit(this.profitCalculationOutputData);
        } else if (this.currentAnnualProfit != null || this.previousAnnualProfit != null) {
            this.setInitialTableData();
            this.updateForm();
            this.profitCalculationOutputData = cloneDeep<IAopProfitCalculation>(this.mapTableDataToOutput());
            this.profitCalculationPrefilledValue.emit(this.profitCalculationOutputData);
        }
    }

    ngOnInit(): void {
        debug('ngOnInit');

        // listen to profit label change
        this.onProfitLabelChange();
        this.applyLocalizations();

        this.listenToAccountForm();
    }

    private getDynamicProfitLabel(): string {
        const currentYear = this.currentAnnualProfit;
        const previousYear = this.previousAnnualProfit;

        // If both years are positive
        if (currentYear > 0 && previousYear > 0) {
            return this.localizedTexts.t(this.local.aumico_annual_report_profit_calculation_annual_profit, {
                lng: this.reportLocale,
            }) as string;
        }

        // If both years are negative
        if (currentYear < 0 && previousYear < 0) {
            return this.localizedTexts.t(this.local.aumico_annual_report_profit_calculation_annual_loss, {
                lng: this.reportLocale,
            }) as string;
        }

        // Mixed results, zeros, or any other scenario
        return this.localizedTexts.t(this.local.aumico_annual_report_profit_calculation_annual_result, {
            lng: this.reportLocale,
        }) as string;
    }

    private getDynamicProfitCalculationHeader(): string {
        const currentYear = this.currentAnnualProfit;
        const previousYear = this.previousAnnualProfit;
        // If both years are positive
        if (currentYear > 0 && previousYear > 0) {
            return this.localizedTexts.t(this.local.aumico_annual_report_profit_calculation_header_profit, {
                lng: this.reportLocale,
            }) as string;
        }
        // If both years are negative
        if (currentYear < 0 && previousYear < 0) {
            return this.localizedTexts.t(this.local.aumico_annual_report_profit_calculation_header_loss, {
                lng: this.reportLocale,
            }) as string;
        }
        // Mixed results, zeros, or any other scenario
        return this.localizedTexts.t(this.local.aumico_annual_report_profit_calculation_header, {
            lng: this.reportLocale,
        }) as string;
    }

    private getDynamicAvailableRetainedEarningText(): string {
        const currentYear = this.currentAnnualProfit;
        const previousYear = this.previousAnnualProfit;
        // If both years are positive
        if (currentYear > 0 && previousYear > 0) {
            return this.localizedTexts.t(this.local.aumico_annual_report_proposal_available_retained_earnings_profit, {
                lng: this.reportLocale,
            }) as string;
        }
        // If both years are negative
        if (currentYear < 0 && previousYear < 0) {
            return this.localizedTexts.t(this.local.aumico_annual_report_proposal_available_retained_earnings_loss, {
                lng: this.reportLocale,
            }) as string;
        }
        // Mixed results, zeros, or any other scenario
        return this.localizedTexts.t(this.local.aumico_annual_report_proposal_available_retained_earnings, {
            lng: this.reportLocale,
        }) as string;
    }

    private applyLocalizations(): void {
        // if no profitCalculation data then this is new mode so we need the default localization value
        if (this.savedProfitCalculation == null) {
            if (this.changeProfitLabel != null) {
                this.annualProfitName.setValue(this.changeProfitLabel);
            } else {
                // Use dynamic label instead of static one
                this.annualProfitName.setValue(this.getDynamicProfitLabel());
            }
            // Set dynamic header text
            this.profitCalcaulationHeaderText = this.getDynamicProfitCalculationHeader();
            // Set dynamic available retained earnings text
            this.availableRetainedEarningText = this.getDynamicAvailableRetainedEarningText();
            this.profitCalculationOutputData = cloneDeep<IAopProfitCalculation>(this.mapTableDataToOutput());
            this.profitCalculationPrefilledValue.emit(this.profitCalculationOutputData);
        }
    }

    private fillFormWithSavedData(): void {
        this.setText(this.savedProfitCalculation);
        this.setInitialTableData();
        this.updateProfitCalculationTableDataWithSavedProfitCalculation();
        this.calculateCurrentAvailableRetainedEarnings();
        this.calculatePreviousAvailableRetainedEarnings();
        this.updateAvailableRetainedEarningsValueInTableData();
        this.updateForm();
    }
    private setText(profitCalculation: IAopProfitCalculation) {
        this.changeProfitLabel = profitCalculation.profitLabel;
        this.annualProfitName.setValue(this.changeProfitLabel);
        this.availableRetainedEarningText = profitCalculation.availableRetainedEarningsText || this.getDynamicAvailableRetainedEarningText();
        this.profitCalcaulationHeaderText = profitCalculation.profitCalcaulationHeaderText || this.getDynamicProfitCalculationHeader();
    }

    private fillFormWithAutofillData(): void {
        this.setText(this.previousProfitCalculation);
        this.setInitialTableData();
        this.updateProfitCalculationTableDataWithAutofillProfitCalculation();
        this.calculatePreviousAvailableRetainedEarnings();
        this.updateForm();
    }

    onAccountSelectionChanged(accountSelection: IAccount): void {
        // REV-20899: the displayed value must include the account's transactional
        // reclass (`closingReClass` / `openingReClass` are populated by the
        // provider's `applyReclassificationsToFinStatements` helper).
        this.accountClosingAmount = ProfitCalculationComponent.finalBalance(
            accountSelection?.calculatedClosingBalance,
            accountSelection?.closingReClass,
        );
        this.accountOpeningAmount = ProfitCalculationComponent.finalBalance(
            accountSelection?.calculatedOpeningBalance,
            accountSelection?.openingReClass,
        );
        this.getFormControl('currentValue', SELECTED_ACCOUNT_INDEX)?.setValue(this.accountClosingAmount);
        this.getFormControl('previousValue', SELECTED_ACCOUNT_INDEX)?.setValue(this.accountOpeningAmount);
        this.getFormControl('customAccountName', SELECTED_ACCOUNT_INDEX)?.setValue(null);
    }

    /** REV-20899: imported balance + transactional reclass; null if neither is finite. */
    private static finalBalance(balance: number | undefined | null, reclass: number | undefined | null): number | null {
        const b = balance != null && NumberUtils.isFinite(balance) ? balance : null;
        const r = reclass != null && NumberUtils.isFinite(reclass) ? reclass : 0;
        if (b == null && r === 0) return null;
        return NumberUtils.roundNumber((b ?? 0) + r);
    }

    private onProfitLabelChange(): void {
        if (this.annualProfitNameChange$ != null) {
            this.annualProfitNameChange$.unsubscribe();
        }
        this.annualProfitNameChange$ = this.annualProfitName.valueChanges
            .pipe(distinctUntilChanged(), untilDestroyed(this))
            .subscribe(() => this.applyProfitLabelChange());
    }

    private applyProfitLabelChange(): void {
        this.profitCalculationOutputData = cloneDeep<IAopProfitCalculation>(this.mapTableDataToOutput());
        debug('profitCalculationOutput', this.profitCalculationOutputData);
        this.profitCalculationOutput.emit(this.profitCalculationOutputData);
    }

    private initializeForm(): void {
        this.profitCalculationTableData = [
            // the first one is for the account selection to appear in the first row
            {
                name: null,
                currentValue: null,
                previousValue: null,
                customAccountName: null,
            },
            {
                name: PROFIT_ROW_NAME,
                currentValue: null,
                previousValue: null,
            },
            {
                name: AVAILABLE_RETAINED_EARNINGS_ROW_NAME,
                currentValue: null,
                previousValue: null,
            },
        ];

        this.form = this.formBuilder.group({
            dataSource: this.formBuilder.array([]),
        });
        this.dataSource = this.getAllAsFormArray();
        this.form.setControl('dataSource', this.dataSource);

        // listen to form changes
        this.onFormValueChange();
    }

    private getAllAsFormArray(): UntypedFormArray {
        const formGroups = this.profitCalculationTableData.map((row) => this.addFormGroup(row));
        return new UntypedFormArray(formGroups);
    }

    private addFormGroup(dataSource: IProfitCalculationTableData): UntypedFormGroup {
        const dataSourceFormGroup = {};
        Object.keys(dataSource).forEach((key) => {
            let value: string | IAccount;
            if (typeof dataSource[key] === 'object' && dataSource[key] != null) {
                value = this.balanceAccounts.find(
                    (balanceAccount) =>
                        balanceAccount.accountNumber === dataSource[key].accountNumber &&
                        balanceAccount.name === dataSource[key].name &&
                        NumberUtils.areNumbersEqual(
                            balanceAccount.calculatedClosingBalance!,
                            dataSource[key].calculatedClosingBalance,
                        ) &&
                        NumberUtils.areNumbersEqual(
                            balanceAccount.calculatedOpeningBalance!,
                            dataSource[key].calculatedOpeningBalance,
                        ),
                );
            } else {
                value = dataSource[key];
            }
            dataSourceFormGroup[key] = new UntypedFormControl(value);
        });
        return new UntypedFormGroup(dataSourceFormGroup);
    }

    private updateForm(): void {
        this.dataSource = this.getAllAsFormArray();
        this.form?.setControl('dataSource', this.dataSource);

        this.listenToAccountForm();
    }

    private listenToAccountForm() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }

        this.subscription = this.getFormControl('name', SELECTED_ACCOUNT_INDEX)
            .valueChanges.pipe(distinctUntilChanged())
            .subscribe((account: IAccount) => {
                this.onAccountSelectionChanged(account);
            });
    }

    private updateProfitCalculationTableDataWithSavedProfitCalculation(): void {
        //edit mode (filling form with saved data)
        this.annualProfitName.setValue(this.savedProfitCalculation.profitLabel, { emitEvent: false });
        if (this.savedProfitCalculation.account != null) {
            const savedSelectedAccount = this.savedProfitCalculation.account;
            // we know that index 0 (first row) is the one that contains the account data
            const selectedAccountFromBalanceAccounts = this.balanceAccounts.find(
                (balanceAccount) =>
                    balanceAccount.accountNumber === savedSelectedAccount.accountNumber &&
                    balanceAccount.name === savedSelectedAccount.name &&
                    NumberUtils.areNumbersEqual(
                        balanceAccount.calculatedClosingBalance!,
                        savedSelectedAccount.calculatedClosingBalance!,
                    ) &&
                    NumberUtils.areNumbersEqual(
                        balanceAccount.calculatedOpeningBalance!,
                        savedSelectedAccount.calculatedOpeningBalance!,
                    ),
            );

            if (this.profitCalculationTableData[SELECTED_ACCOUNT_INDEX] != null) {
                this.profitCalculationTableData[SELECTED_ACCOUNT_INDEX].name = selectedAccountFromBalanceAccounts;
                this.profitCalculationTableData[SELECTED_ACCOUNT_INDEX].customAccountName =
                    this.savedProfitCalculation.customAccountName;

                // REV-20899: prefer the CURRENT balance account's reclass (the
                // saved snapshot is from when the user last saved and is stale
                // if a reclass was added since). Fall back to the saved values
                // when no current match is found.
                const reclassSourceAccount: IAccount =
                    selectedAccountFromBalanceAccounts ?? this.savedProfitCalculation.account;
                const balanceSource: IAccount = this.savedProfitCalculation.account;

                const selectedAccountClosingValue = ProfitCalculationComponent.finalBalance(
                    balanceSource.calculatedClosingBalance,
                    reclassSourceAccount.closingReClass,
                );
                if (selectedAccountClosingValue != null) {
                    this.profitCalculationTableData[SELECTED_ACCOUNT_INDEX].currentValue = selectedAccountClosingValue;
                    this.accountClosingAmount = selectedAccountClosingValue;
                }

                const selectedAccountOpeningValue = ProfitCalculationComponent.finalBalance(
                    balanceSource.calculatedOpeningBalance,
                    reclassSourceAccount.openingReClass,
                );
                if (selectedAccountOpeningValue != null) {
                    this.profitCalculationTableData[SELECTED_ACCOUNT_INDEX].previousValue = selectedAccountOpeningValue;
                    this.accountOpeningAmount = selectedAccountOpeningValue;
                }
            }
        }
        this.savedProfitCalculation.profitEntries.forEach((entry) => {
            const startingIndexNumberForEntries = this.profitCalculationTableData.length - 2;
            // TODO: Check it to be implemented in a clearer way
            // we need to add a new row directly above the last 2 rows (last 2 objects in profitCalculationTableData)
            let currentValue: number | null = null;
            let previousValue: number | null = null;

            if (entry.currentValue != null && NumberUtils.isFinite(entry.currentValue)) {
                currentValue = NumberUtils.roundNumber(entry.currentValue);
            }
            if (entry.previousValue != null && NumberUtils.isFinite(entry.previousValue)) {
                previousValue = NumberUtils.roundNumber(entry.previousValue);
            }
            this.profitCalculationTableData.splice(startingIndexNumberForEntries, FIRST_ELEMENT_INDEX, {
                name: entry.name,
                currentValue,
                previousValue,
            });
        });
        this.profitCalculationOutputData = cloneDeep<IAopProfitCalculation>(this.mapTableDataToOutput());
        debug('profitCalculationOutput', this.profitCalculationOutputData);
        this.profitCalculationPrefilledValue.emit(this.profitCalculationOutputData);
    }

    private updateProfitCalculationTableDataWithAutofillProfitCalculation(): void {
        // set account
        if (this.previousProfitCalculation.account != null) {
            const selectedAccountFromBalanceAccounts = this.balanceAccounts.find(
                (balanceAccount) =>
                    balanceAccount.accountNumber === this.previousProfitCalculation.account.accountNumber &&
                    balanceAccount.name === this.previousProfitCalculation.account.name,
            );

            // Check if the account was actually found
            if (selectedAccountFromBalanceAccounts) {
                this.profitCalculationTableData[SELECTED_ACCOUNT_INDEX].name = selectedAccountFromBalanceAccounts;
                this.profitCalculationTableData[SELECTED_ACCOUNT_INDEX].customAccountName =
                    this.previousProfitCalculation.customAccountName;

                const selectedAccountClosingValue = NumberUtils.roundNumber(
                    selectedAccountFromBalanceAccounts.calculatedClosingBalance,
                );
                const selectedAccountOpeningValue = NumberUtils.roundNumber(
                    selectedAccountFromBalanceAccounts.calculatedOpeningBalance,
                );

                this.profitCalculationTableData[SELECTED_ACCOUNT_INDEX].currentValue = selectedAccountClosingValue;
                this.profitCalculationTableData[SELECTED_ACCOUNT_INDEX].previousValue = selectedAccountOpeningValue;
                this.accountClosingAmount = selectedAccountClosingValue;
                this.accountOpeningAmount = selectedAccountOpeningValue;
            } else {
                // Optional: Log a warning or handle the missing account case
                console.warn('Previously selected was removed or renamed');
            }
        }
        // set entries

        this.previousProfitCalculation.profitEntries.forEach((entry) => {
            const startingIndexNumberForEntries = this.profitCalculationTableData.length - 2;
            // TODO: Check it to be implemented in a clearer way
            // we need to add a new row directly above the last 2 rows (last 2 objects in profitCalculationTableData)
            let currentValue: number | null = null;

            if (entry.currentValue != null && NumberUtils.isFinite(entry.currentValue)) {
                currentValue = NumberUtils.roundNumber(entry.currentValue);
            }

            this.profitCalculationTableData.splice(startingIndexNumberForEntries, FIRST_ELEMENT_INDEX, {
                name: entry.name,
                currentValue: null,
                previousValue: currentValue,
            });
        });
        this.profitCalculationOutputData = cloneDeep<IAopProfitCalculation>(this.mapTableDataToOutput());
        this.profitCalculationPrefilledValue.emit(this.profitCalculationOutputData);
    }

    private onFormValueChange(): void {
        if (this.formChanges != null) {
            this.formChanges.unsubscribe();
        }
        this.formChanges = this.form.valueChanges
            .pipe(
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe((value: { dataSource: IProfitCalculationTableData[] }) => this.onFormChange(value));
    }

    private onFormChange(value: { dataSource: IProfitCalculationTableData[] }): void {
        this.profitCalculationTableData = value.dataSource;
        this.calculateCurrentAvailableRetainedEarnings();
        this.calculatePreviousAvailableRetainedEarnings();
        this.profitCalculationOutputData = cloneDeep<IAopProfitCalculation>(this.mapTableDataToOutput());
        debug('profitCalculationOutput', this.profitCalculationOutputData);
        this.profitCalculationOutput.emit(this.profitCalculationOutputData);
    }

    private updateAvailableRetainedEarningsValueInTableData(): void {
        const availabeRetainedEarningsIndex = this.profitCalculationTableData.findIndex(
            (entry) => entry.name === AVAILABLE_RETAINED_EARNINGS_ROW_NAME,
        );
        if (availabeRetainedEarningsIndex != null) {
            this.profitCalculationTableData[availabeRetainedEarningsIndex].currentValue =
                this.currentAvailableRetainedEarnings;
            this.profitCalculationTableData[availabeRetainedEarningsIndex].previousValue =
                this.previousAvailableRetainedEarnings;
        }
    }

    private calculateCurrentAvailableRetainedEarnings(): void {
        const entriesValue = this.profitCalculationTableData.map((entry) => entry.currentValue);
        const sumOfEntriesValue = NumberUtils.sumIgnoringInfiniteNumbers(entriesValue, undefined);

        // we must deduct the last entry value because it has the available retained earnings value and the amount will be duplicated
        const lastRowValue = this.profitCalculationTableData[this.profitCalculationTableData.length - 1].currentValue;
        let lastRowRoundedValue: number | null = null;
        if (lastRowValue != null && NumberUtils.isFinite(lastRowValue)) {
            lastRowRoundedValue = NumberUtils.roundNumber(lastRowValue);
        }

        if (
            (sumOfEntriesValue != null && NumberUtils.isFinite(sumOfEntriesValue)) ||
            (lastRowRoundedValue != null && NumberUtils.isFinite(lastRowRoundedValue))
        ) {
            this.currentAvailableRetainedEarnings = NumberUtils.subtractNumbers(
                sumOfEntriesValue ?? 0,
                lastRowRoundedValue ?? 0,
            );
        } else {
            this.currentAvailableRetainedEarnings = null;
        }
        this.profitCalculationCurrentRetainedEarnings.emit(this.currentAvailableRetainedEarnings);
    }

    private calculatePreviousAvailableRetainedEarnings(): void {
        const entriesValue = this.profitCalculationTableData.map((entry) => entry.previousValue) as number[];

        const sumOfEntriesValue = NumberUtils.sumIgnoringInfiniteNumbers(entriesValue, undefined);

        // we must deduct the last entry value because it has the available retained earnings value and the amount will be duplicated
        const lastRowValue = this.profitCalculationTableData[this.profitCalculationTableData.length - 1].previousValue;
        let lastRowRoundedValue: number | null = null;
        if (lastRowValue != null && NumberUtils.isFinite(lastRowValue)) {
            lastRowRoundedValue = NumberUtils.roundNumber(lastRowValue);
        }

        if (
            (sumOfEntriesValue != null && NumberUtils.isFinite(sumOfEntriesValue)) ||
            (lastRowRoundedValue != null && NumberUtils.isFinite(lastRowRoundedValue))
        ) {
            this.previousAvailableRetainedEarnings = NumberUtils.subtractNumbers(
                sumOfEntriesValue ?? 0,
                lastRowRoundedValue ?? 0,
            );
        } else {
            this.previousAvailableRetainedEarnings = null;
        }
        this.profitCalculationPreviousRetainedEarnings.emit(this.previousAvailableRetainedEarnings);
    }

    getFormControl(prop: string, row: number): UntypedFormControl | undefined {
        if (this.form != null) {
            return this.form.get('dataSource')['controls'][row]['controls'][prop];
        } else {
            return undefined;
        }
    }

    addRow(): void {
        const startingIndexNumberForEntries = this.profitCalculationTableData.length - 2;
        // we need to add a new row directly above the last 2 rows (last 2 objects in profitCalculationTableData)
        this.profitCalculationTableData.splice(startingIndexNumberForEntries, FIRST_ELEMENT_INDEX, {
            name: null,
            currentValue: null,
            previousValue: null,
        });
        this.updateForm();
    }

    removeAt(index: number): void {
        debug('remove index', index);
        this.profitCalculationTableData.splice(index, 1);
        this.calculateCurrentAvailableRetainedEarnings();
        this.calculatePreviousAvailableRetainedEarnings();
        this.updateForm();
        this.profitCalculationOutputData = cloneDeep<IAopProfitCalculation>(this.mapTableDataToOutput());
        debug('profitCalculationOutput', this.profitCalculationOutputData);
        this.profitCalculationOutput.emit(this.profitCalculationOutputData);
    }

    private setInitialTableData(): void {
        // initially, available retained earnings is equal to annual profit if there are no other entries
        this.currentAvailableRetainedEarnings = this.currentAnnualProfit;
        this.previousAvailableRetainedEarnings = this.previousAnnualProfit;

        const dynamicProfitLabel = this.getDynamicProfitLabel();

        this.profitCalculationTableData = [
            // the first one is for the select to appear in the first row
            {
                name: null,
                currentValue: this.accountClosingAmount,
                previousValue: this.accountOpeningAmount,
                customAccountName: null,
            },
            {
                name: dynamicProfitLabel,
                currentValue: this.currentAnnualProfit,
                previousValue: this.previousAnnualProfit,
            },
            {
                name: AVAILABLE_RETAINED_EARNINGS_ROW_NAME,
                currentValue: this.currentAvailableRetainedEarnings,
                previousValue: this.previousAvailableRetainedEarnings,
            },
        ];
    }

    private mapTableDataToOutput(): IAopProfitCalculation {
        let account: IAccount;
        let customAccountName: string | null;
        const profitEntries: IAppropriationOfProfitEntry[] = [];

        this.profitCalculationTableData.forEach((tableRow: IProfitCalculationTableData, index: number) => {
            // The first index has the selected account
            if (index === SELECTED_ACCOUNT_INDEX) {
                // get account object using account id
                account = tableRow.name as IAccount;
                customAccountName = tableRow.customAccountName;
                // we need to skip the last 2 rows (annual profit & retained earnings)
            } else if (
                index !== this.profitCalculationTableData.length - 1 &&
                index !== this.profitCalculationTableData.length - 2
            ) {
                let currentValue: number | null = null;
                let previousValue: number | null = null;

                if (tableRow.currentValue != null && NumberUtils.isFinite(tableRow.currentValue)) {
                    currentValue = NumberUtils.roundNumber(tableRow.currentValue);
                }
                if (tableRow.previousValue != null && NumberUtils.isFinite(tableRow.previousValue)) {
                    previousValue = NumberUtils.roundNumber(tableRow.previousValue);
                }
                profitEntries.push({
                    name: tableRow.name as string,
                    currentValue: currentValue,
                    previousValue: previousValue,
                });
            }
        });

        return {
            account: account,
            profitLabel: this.annualProfitName.value,
            profitEntries: profitEntries,
            customAccountName: customAccountName,
            availableRetainedEarningsText:
                this.availableRetainedEarningText ?? this.getDynamicAvailableRetainedEarningText(),
            profitCalcaulationHeaderText:
                this.profitCalcaulationHeaderText ?? this.getDynamicProfitCalculationHeader(),
        };
    }

    public compareAccounts(account1: IAccount, account2: IAccount): boolean {
        let calculatedClosingBalanceIsEqual = false;
        let calculatedOpeningBalanceIsEqual = false;
        if (account1.calculatedClosingBalance == null && account2.calculatedClosingBalance == null) {
            calculatedClosingBalanceIsEqual = true;
        } else if (account1.calculatedClosingBalance != null && account2.calculatedClosingBalance != null) {
            calculatedClosingBalanceIsEqual = NumberUtils.areNumbersEqual(
                account1.calculatedClosingBalance,
                account2.calculatedClosingBalance,
            );
        } else {
            return false;
        }
        if (account1.calculatedOpeningBalance == null && account2.calculatedOpeningBalance == null) {
            calculatedOpeningBalanceIsEqual = true;
        } else if (account1.calculatedOpeningBalance != null && account2.calculatedOpeningBalance != null) {
            calculatedOpeningBalanceIsEqual = NumberUtils.areNumbersEqual(
                account1.calculatedOpeningBalance,
                account2.calculatedOpeningBalance,
            );
        } else {
            return false;
        }

        return (
            account1.accountNumber === account2.accountNumber &&
            account1.name === account2.name &&
            calculatedClosingBalanceIsEqual &&
            calculatedOpeningBalanceIsEqual
        );
    }

    public onSaveAvailableRetainedEarnings(availabeRetainedEarningsText: string): void {
        this.availableRetainedEarningText = availabeRetainedEarningsText;
        this.profitCalculationOutputData.availableRetainedEarningsText = availabeRetainedEarningsText;
        this.profitCalculationOutput.emit(this.profitCalculationOutputData);
    }

    public onSaveProfitCalculationText(profitCalculationText: string): void {
        this.profitCalcaulationHeaderText = profitCalculationText;
        this.profitCalculationOutputData.profitCalcaulationHeaderText = profitCalculationText;
        this.profitCalculationOutput.emit(this.profitCalculationOutputData);
    }
}
